<template>
  <v-container fluid class="px-0 py-0" v-resize="onResize">
    <slot name="bar">
      <v-system-bar height="24">
        <span v-if="!!title"><span>{{ title }}</span></span>
        <v-spacer></v-spacer>
        <CommonSwitchFilterGroup
            v-if="!!state.common && !!state.common.bar_switches && state.common.bar_switches.length > 0"
            v-model="state.common.bar_switches"
            :filter-names.sync="state.selectedSwitches"
            x-small
        ></CommonSwitchFilterGroup>
        <CommonRefresh v-if="items === null" :loading="state.loading" @click="update()"></CommonRefresh>
      </v-system-bar>
    </slot>
    <v-data-iterator
        v-model="state.selected"
        :items="itemList"
        item-key="uid"
        class="elevation-1"
        :options.sync="state.options"
        :server-items-length="serverItemsLength"
        :show-select="showSelect"
        :single-select="singleSelect"
        :footer-props="state.footerProps"
    >
      <template v-slot:header>
        <slot name="top">
          <v-toolbar dense :height="heightToolbar">
            <slot name="top.bar">
              <v-row class="flex-nowrap">
                <slot name="top.bar.status">
                  <CommonFilterGroup
                      v-if="items === null && !!state.common && !!state.common.bar_statuses && state.common.bar_statuses.length > 0"
                      v-model="state.common.bar_statuses"
                      :filter-names.sync="state.selectedStatuses"
                  ></CommonFilterGroup>
                </slot>
                <slot name="top.bar.filter">
                  <CommonFilterGroup
                      v-if="items === null && !!state.common && !!state.common.bar_filters && state.common.bar_filters.length > 0"
                      v-model="state.common.bar_filters"
                      :filter-names.sync="state.selectedFilters"
                  ></CommonFilterGroup>
                </slot>
                <slot name="top.bar.info"></slot>
              </v-row>
              <slot name="top.bar.search">
                <v-container fluid>
                  <BaseFieldString v-model="state.search" label="Search" dense clearable=""></BaseFieldString>
                </v-container>
              </slot>
              <slot name="top.bar.action">
                <slot name="top.bar.action.left"></slot>
                <slot name="top.bar.action.middle">
                </slot>
                <slot name="top.bar.action.right"></slot>
              </slot>
              <slot name="top.bar.overlay"></slot>
            </slot>
          </v-toolbar>
        </slot>
        <v-divider></v-divider>
      </template>
      <slot name="footer.prepend">
        <template v-slot:footer.prepend>
          <CommonBtnActionGroup
              v-if="!!state.common && !!state.common.bar_actions && state.common.bar_actions.length > 0"
              v-model="state.common.bar_actions"
              :selected-items="state.selected"
              :filter-names.sync="state.selectedFilters"
              @action="onAction($event)"
              @embedded_new="openDetailDialog(null)"
          ></CommonBtnActionGroup>
        </template>
      </slot>
      <template v-slot:default="props">
        <v-card :height="height || componentHeight" class="px-3 overflow-y-auto">
          <v-row>
            <v-col
                v-for="item in props.items"
                :key="item.name"
                cols="12"
                :sm="grid.sm"
                :md="grid.md"
                :lg="grid.lg"
                :xl="grid.xl"
            >
              <slot name="card" :item="item">
                <v-card>
                  <v-system-bar height="24" class="px-0">
                    <slot name="card.bar">
                      <v-chip small>uid: {{ item.uid }}</v-chip>
                      <v-chip small>{{ $t('version') }}: {{ item.version }}</v-chip>
                      <v-spacer></v-spacer>
                      <BaseDeleteIcon
                          v-if="!!aggregateStore && !!aggregateStore.rootData"
                          v-model="aggregateStore.rootData[item.uid].deleted"
                          :readonly="false"
                          :show_label="false"
                      ></BaseDeleteIcon>
                      <CommonIsSaved
                          v-if="!!aggregateStore && !!aggregateStore.rootData"
                          small
                          :value="aggregateStore.rootData[item.uid].is_saved"
                      ></CommonIsSaved>
                    </slot>
                  </v-system-bar>
                  <slot name="card.body" :item="item">
                    <v-card height="200" class="overflow-y-auto">
                      {{ item }}
                    </v-card>
                  </slot>
                </v-card>
              </slot>
            </v-col>
          </v-row>
        </v-card>
      </template>
    </v-data-iterator>
    <v-dialog
        ref="detailDialog"
        v-model="stateDetail.dialogDetail"
        persistent
        scrollable
        v-if="!!importDetailComponent & !!detailComponentPass"
    >
      <component
          :uid-item="stateDetail.uidItem"
          :is="importDetailComponent"
          :store-name="detailStoreName"
          :dto-detail-query="dtoDetailQuery"
          :modalDialog="true"
          :route-full-screen="{name: detailComponent, params: {itemUid: stateDetail.uidItem}}"
          :parent-component="parentComponent"
          :main-validator="true"
          @close="update()"
      ></component>
    </v-dialog>
  </v-container>
</template>

<script>
import Vuetify from 'vuetify'
import {useAggregateStore} from '@/store/CommonStore/baseAggregateStore'
import {defineStore} from "pinia";
import {debounce} from "debounce";
import {v4 as uuidv4} from "uuid";
import CommonFilterGroup from "@/components/Common/CommonFilterGroup";
import BaseFieldString from "@/components/Base/BaseFieldString";
import CommonBtnActionGroup from "@/components/Common/CommonBtnActionGroup";
import BaseTextSwitch from "@/components/Base/BaseTextSwitch";
import CommonSwitchFilterGroup from "@/components/Common/CommonSwitchFilterGroup";
import BaseStatusIcon from "@/components/Base/BaseStatusIcon";
import {useDetailStore} from "@/components/Common/CommonDetail";
import CommonRefresh from "@/components/Common/CommonRefresh";
import CommonToolbar from "@/components/Common/CommonToolbar";
import CommonIsSaved from "@/components/Common/CommonIsSaved";
import BaseDeleteIcon from "@/components/Base/BaseDeleteIcon";

export const useCardListStore = function (storeName, wsUrl) {
  let aggregateStore = useAggregateStore()
  return defineStore(storeName, {
        state: () => ({
          uidList: [],
          headers: [],
          loading: false,
          selected: [],
          common: {},
          selectedStatuses: null,
          selectedFilters: null,
          selectedSwitches: null,

          search: null,
          filters: [],

          height: null,

          options: {
            page: 1,
            itemsPerPage: 20,
            sortBy: ['uid'],
            sortDesc: [false],
            multiSort: false,
            mustSort: true,
            descending: false,
          },
          footerProps: {'items-per-page-options': [5, 20, 50, 100, 200, -1]},

          confirm_request: {
            dialog: false,
            message: null,
            request: {},
            type: 'info',
            timeout: -1,
          },
          showSelect: true,
          singleSelect: false,
          topToolbar: [],
          bottomToolbar: [],
          serverItemsLength: 0,
          items: null,
          dtoListQuery: null,
        }),
        getters: {
          proxyList() {
            let result = []
            if (!!aggregateStore && !!aggregateStore.rootData)
              this.uidList.forEach(uid => result.push(aggregateStore.rootData[uid]))
            return result
          },
        },
        actions: {
          callReceive() {
            this.loading = false
          },
          callBack() {
          },
          setExcludeFilters(uidList) {
            this.filters['exclude_uid'] = uidList
          },
          update() {
            if (this.items !== null)
              return
            this.loading = true
            aggregateStore.fetchList({
              storeName: storeName,
              dtoListQuery: this.dtoListQuery,
              search: this.search,
              filters: this.filters,
              options: this.options,
              selectedStatuses: this.selectedStatuses,
              selectedFilters: this.selectedFilters,
              selectedSwitches: this.selectedSwitches,
              callBack: this.callBack
            })
          }
        },
      }
  )
}

export default {
  name: "CommonCardList",

  props: {
    height: {default: null},
    items: {default: null},
    dtoListQuery: {type: [String], default: null},
    dtoDetailQuery: {type: [String], default: null},
    dtoValidateCommand: {type: [String], default: null},
    dtoActionCommand: {type: [String], default: null},
    dtoToolbarQuery: {type: [String], default: null},
    storeName: {type: [String]},
    detailStoreName: {type: [String]},
    mainValidator: {type: [Boolean], default: false},
    title: {type: [String], default: null},

    heightToolbar: {type: [Number], default: 48},
    detailComponentPass: {type: [String], default: null},
    parentComponent: {type: [String], default: null},
    defaultNewItem: {
      type: [Function], default() {
        return {uid: uuidv4()}
      }
    },

    showSelect: {default: true},
    singleSelect: {default: false},
    verticalGrid: {
      default() {
        return {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 6,
          xl: 3,
        };
      }
    },
    horizontalGrid: {
      default() {
        return {
          xs: 12,
          sm: 12,
          md: 6,
          lg: 4,
          xl: 3,
        };
      }
    },
  },

  components: {
    'add-component': () => import(self.detailComponentPass),
    CommonFilterGroup,
    BaseFieldString,
    CommonBtnActionGroup,
    BaseTextSwitch,
    CommonSwitchFilterGroup,
    BaseStatusIcon,
    CommonRefresh,
    CommonToolbar,
    CommonIsSaved,
    BaseDeleteIcon,
  },

  computed: {
    itemList() {
      if (this.items === null)
        return this.state.proxyList
      return this.items
    },
    serverItemsLength() {
      if (this.items === null)
        return this.state.serverItemsLength
      return this.items.length
    },
    queryParameters() {
      return {
        search: this.state.search,
        options: this.state.options,
        selectedStatuses: this.state.selectedStatuses,
        selectedFilters: this.state.selectedFilters,
        selectedSwitches: this.state.selectedSwitches,
        filters: this.state.filters,
      }
    },
    detailComponent() {
      if (!!this.detailComponentPass)
        return this.detailComponentPass.split('/').pop()
    },
    importDetailComponent() {
      if (!!this.detailComponentPass) {
        return () => import("@/components" + this.detailComponentPass);
      }
    },
    componentHeight() {
      if (this.$vuetify.breakpoint.mobile)
        return this.$vuetify.breakpoint.height - 140 - 64
      return this.$vuetify.breakpoint.height - 140
    },
    enableRootData() {
      return !!this.aggregateStore && !!this.aggregateStore.rootData && !!this.aggregateStore.rootData[this.state.uidItem]
    },
  },

  watch: {
    queryParameters: {
      handler(newValue, oldValue) {
        // console.log('watch queryParameters')
        this.update()
      },
      deep: true
    },
  },

  setup({storeName, detailStoreName, showSelect, singleSelect, height, items, dtoListQuery}) {
    let state = useCardListStore(storeName)()
    let stateDetail = useDetailStore(detailStoreName)()
    state.showSelect = showSelect
    state.singleSelect = singleSelect
    state.height = height
    state.items = items
    state.dtoListQuery = dtoListQuery
    return {state, stateDetail}
  },

  async mounted() {
    // console.log('mount stateDetail', this.stateDetail)
    this.aggregateStore = useAggregateStore()
    if (this.mainValidator === true) {
      this.aggregateStore.dtoValidateCommand = this.dtoValidateCommand
    }
    this.onResize()
  },

  methods: {
    onResize() {
      this.windowSize = {x: window.innerWidth, y: window.innerHeight};
      this.horizontal = this.windowSize.x > this.windowSize.y
      if (this.horizontal)
        this.grid = this.horizontalGrid
      else
        this.grid = this.verticalGrid
    },
    openDetailDialog(item) {
      // console.log('openDetailDialog', item)
      if (!!item) {
        this.stateDetail.uidItem = item.uid
      } else {
        let defaultNewItem = this.defaultNewItem()
        this.stateDetail.uidItem = defaultNewItem.uid
        this.aggregateStore.rootData[defaultNewItem.uid] = defaultNewItem
        // console.log('new openDetailDialog', defaultNewItem)
        // this.aggregateStore.updateData()
      }
      this.stateDetail.dialogDetail = true
      // console.log('openDetailDialog', item.uid, this.stateDetail)
    },
    update: debounce(function () {
      if (this.items !== null)
        return
      this.state.loading = true
      this.aggregateStore.fetchList({
        storeName: this.storeName,
        dtoListQuery: this.dtoListQuery,
        search: this.state.search,
        filters: this.state.filters,
        options: this.state.options,
        selectedStatuses: this.state.selectedStatuses,
        selectedFilters: this.state.selectedFilters,
        selectedSwitches: this.state.selectedSwitches,
        callBack: this.state.callBack
      })
    }, 500),
    onAction(actionName) {
      let selectedUid = []
      this.state.selected.forEach(row => {
        selectedUid.push(row.uid)
      })
      this.aggregateStore.fetchAction({
        storeName: this.storeName,
        dtoActionCommand: this.dtoActionCommand,
        uidItem: null,
        uidSelected: selectedUid,
        actionName: actionName,
        callBack: null
      })
    },
  },

  data() {
    return {
      self: this,
      aggregateStore: null,
      windowSize: {},
      horizontal: false,
      grid: {},
    }
  },
}
</script>

<style scoped>

</style>

<i18n src="@/components/Common/locales.json">
</i18n>